import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth-store'
import { LoginSheet } from './login-sheet'

/**
 * 认证的宿主：首屏恢复登录态，并在需要时弹出登录面板。
 *
 * ⚠⚠ **未登录时这里一个请求都不发、一帧「加载中」都不闪**(FE-1)：
 *   auth store 的 `ready` 初值就是「本地没有令牌」，所以无令牌时下面的 `useEffect`
 *   压根不会调 `boot()`。产品第 1 点「打开直接看到聊天窗口」与「断网也照样出界面」
 *   靠的就是这一点 —— 方案 B 时代这里必然要等一次网络往返(领匿名令牌)。
 *
 * ⚠ 有令牌时仍然要等 A7 回来：那一屏本来就要显示「我的」等用户信息，
 *   拿旧缓存先渲染再改，比等一次往返更糟(等级可能被后台改过)。
 *
 * ⚠ 挂在根布局上，全应用只有这一处 —— 登录面板不是页面，是覆盖层，
 *   因为「登录后原地恢复」要求当前页面不能被卸载。
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const ready = useAuthStore((s) => s.ready)
  const loginOpen = useAuthStore((s) => s.loginOpen)
  const boot = useAuthStore((s) => s.boot)

  useEffect(() => {
    if (!ready) void boot()
  }, [ready, boot])

  if (!ready) {
    return (
      <main className="flex min-h-full items-center justify-center p-6">
        <p className="text-sm text-neutral-400">{t('common.loading')}</p>
      </main>
    )
  }

  return (
    <>
      {children}
      {loginOpen ? <LoginSheet /> : null}
    </>
  )
}
